import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  getArchitecturalRules,
  getDeterministicRules,
  validateMap,
  validateMapStrict,
  streamCode,
  streamCodeFromIntent,
  validateIntake,
  generateMapFromIntent,
  generateMapStream,
  analyzeIntent,
  diagnoseError,
  extractText,
  getMe,
  verifyCheckoutSession,
  submitQuoteRequest,
  generateMicroIntelligenceMap,
  updateSessionMicroMap,
  approveMapOnServer,
  getMyProject,
  saveMyProjectState
} from "../api/apiClient";
import JSZip from "jszip";
import { Sandpack } from "@codesandbox/sandpack-react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ActivityPanel from "../components/ActivityPanel";
import SecondPairOfEyesCard from "../components/SecondPairOfEyesCard";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GavelIcon from "@mui/icons-material/Gavel";
import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import SecurityIcon from "@mui/icons-material/Security";
import StorageIcon from "@mui/icons-material/Storage";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BugReportIcon from "@mui/icons-material/BugReport";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { validateMapLocal, SAMPLE_MAP_JSON } from "../mock/validateMapLocal";
import FinalBlueprintOutputs from "../components/FinalBlueprintOutputs";
import GenerationProgress, { useStagedProgress } from "../components/GenerationProgress";
import HCLLiteView from "../components/hcl/HCLLiteView";
import {
  appendValidationHistory,
  clearAllLocalWorkflow,
  loadApprovedSnapshot,
  loadDraftMap,
  loadGeneratedCode,
  loadValidationHistory,
  saveApprovedSnapshot,
  saveDraftMap,
  saveGeneratedCode,
  getGuestTrialStatus,
  recordGuestTrialAttempt,
} from "../mock/localWorkflowStore";
import { mockGenerateFromApprovedMap } from "../mock/mockClaudeGenerate";
import { runLiveMonitor } from "../mock/mockMonitoring";
import { getToken, isAuthenticated } from "../api/session";

// Stage labels for the staged progress indicators. Each stage is marked
// complete only when its underlying request actually settles — honest
// milestones, not simulated backend claims.
export const INTELLIGENCE_STAGES = [
  "Preparing request",
  "Validating requirements",
  "Analyzing intent",
  "Building micro-intelligence map",
  "Finalizing Intelligence Report",
];
export const MAP_GEN_STAGES = [
  "Preparing blueprint request",
  "Generating flow map",
  "Rendering blueprint JSON",
];

const statusColor = (s) => {
  if (s === "pass") return "success";
  if (s === "pass_with_warnings") return "warning";
  return "error";
};

// Business-friendly labels of everything the clarification flow already
// resolved from the description / uploaded documents / earlier answers.
export const understoodLabels = (summary) => {
  const state = summary?.project_state;
  if (!state) return [];
  const reqs = state.requirements || {};
  const labels = (state.explicit_requirements || []).map(
    (k) => reqs[k]?.label || k.replace(/_/g, " ")
  );
  return labels.filter((l, i) => labels.indexOf(l) === i);
};

const decisionColor = (d) => {
  if (d === "ASK") return "warning";
  if (d === "DEFAULT") return "info";
  if (d === "INFER") return "secondary";
  return "default";
};

const summarizeValidationBuckets = (issues = []) => {
  const isGap = (msg = "", locationType = "") => {
    const text = `${msg} ${locationType}`.toLowerCase();
    return (
      text.includes("missing") ||
      text.includes("required") ||
      text.includes("not defined") ||
      text.includes("section") ||
      text.includes("field")
    );
  };

  const isConflict = (msg = "") => {
    const text = msg.toLowerCase();
    return (
      text.includes("ambiguous") ||
      text.includes("conflict") ||
      text.includes("bypass") ||
      text.includes("shortcut")
    );
  };

  let gaps = 0;
  let conflicts = 0;
  for (const i of issues) {
    if (isConflict(i.message)) conflicts += 1;
    else if (isGap(i.message, i.location_type)) gaps += 1;
  }

  return {
    totalIssues: issues.length,
    gaps,
    conflicts,
  };
};

export default function CodingAssistantPage() {
  const navigate = useNavigate();
  const [mapText, setMapText] = useState();
  const [result, setResult] = useState(null);

  // -- Intake States (Step 0) --
  const [rawIntake, setRawIntake] = useState("");
  const [isIntaking, setIsIntaking] = useState(false);
  const [intakeContract, setIntakeContract] = useState(null);
  const [intakeError, setIntakeError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [answerInput, setAnswerInput] = useState("");
  const [isGeneratingMap, setIsGeneratingMap] = useState(false);
  const [intentSummary, setIntentSummary] = useState(null);
  const [microMapResult, setMicroMapResult] = useState(null);
  const [sessionId, setSessionId] = useState("");
  // Set when the user opened an existing project from the dashboard. Drives
  // both the resume-on-load and the autosave below.
  const [resumedProjectId, setResumedProjectId] = useState(null);
  const [isResuming, setIsResuming] = useState(false);
  const [resumeError, setResumeError] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error

  const [selectedFileTab, setSelectedFileTab] = useState(0);
  const [isLivePreview, setIsLivePreview] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [runError, setRunError] = useState("");
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState(null);

  // -- Pricing / User State --
  const [userProfile, setUserProfile] = useState(null);
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // -- Quote Form State --
  const [quoteFormOpen, setQuoteFormOpen] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [quoteFormErrors, setQuoteFormErrors] = useState({});
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  // -----------------------------
  const outletContext = useOutletContext();
  const setActiveStep = outletContext?.setActiveStep || (() => { });

  const [approved, setApproved] = useState(false);
  const [approvedLockedJson, setApprovedLockedJson] = useState(null);
  // Server-side approval record ({ blueprintId, mapId, version }) — null when
  // approval is client-side only (guest sessions / server unavailable).
  const [blueprintRecord, setBlueprintRecord] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [history, setHistory] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isExported, setIsExported] = useState(false);
  const [architecturalRules, setArchitecturalRules] = useState([]);
  const [deterministicRules, setDeterministicRules] = useState([]);
  const [viewMode, setViewMode] = useState("all");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // --- Step-completion / progress / auto-advance state -------------------
  // Text that was last successfully analyzed: identical input ⇒ the intake
  // button stays disabled (completed) instead of allowing a duplicate run.
  const [lastAnalyzedText, setLastAnalyzedText] = useState(null);
  // True once the flow map was generated for the current analysis.
  const [mapGenerated, setMapGenerated] = useState(false);
  // Map text that last passed validation; editing the JSON re-enables the button.
  const [validatedText, setValidatedText] = useState(null);

  const intelProgress = useStagedProgress(INTELLIGENCE_STAGES.length);
  const mapProgress = useStagedProgress(MAP_GEN_STAGES.length);

  // Same-tick double-click guards (state updates are async).
  const intakeInFlightRef = useRef(false);
  const generateInFlightRef = useRef(false);

  // Auto-advance targets.
  const answerInputRef = useRef(null);
  const generateAreaRef = useRef(null);
  const validatorSectionRef = useRef(null);
  const approvalSectionRef = useRef(null);
  const outputsSectionRef = useRef(null);

  const advanceTo = (ref, block = "start") => {
    setTimeout(() => {
      ref.current?.scrollIntoView?.({ behavior: "smooth", block });
    }, 400);
  };

  const processFile = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedExtensions = ['.txt', '.doc', '.docx', '.pdf', '.md'];
    const fileName = file.name.toLowerCase();
    const isValid = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValid) {
      setIntakeError("Please upload only TXT, Word, PDF, or MD files");
      return;
    }

    setIsUploading(true);
    setIntakeError(null);
    try {
      const res = await extractText(file);
      if (res.text) {
        // Tag the document so the backend can record requirement provenance
        // (uploaded_document vs. the typed description) and avoid asking for
        // anything the specification already answers.
        const tagged = `[Uploaded Document: ${file.name}]\n${res.text.trim()}\n[End of Uploaded Document]`;
        setRawIntake(prev => prev ? prev + "\n\n" + tagged : tagged);
      }
    } catch (err) {
      setIntakeError(err.response?.data?.detail || "File upload failed: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (file) await processFile(file);
    // Reset input so the same file can be selected again
    event.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await processFile(e.dataTransfer.files[0]);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');

    const initialize = async () => {
      let paymentSuccess = false;

      if (payment) {
        if (payment === 'success' && sessionId) {
          try {
            const verifyRes = await verifyCheckoutSession(sessionId);
            if (verifyRes.status === 'success' || verifyRes.status === 'already_processed') {
              setPaymentStatus('success');
              paymentSuccess = true;
            } else {
              setPaymentStatus('error');
            }
          } catch (err) {
            console.error("Session verification failed", err);
            setPaymentStatus('error');
          }
        } else {
          setPaymentStatus('error');
        }

        setSnackbarOpen(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // After verifying payment (which might have updated the DB), fetch user
      const token = getToken();
      if (token) {
        try {
          const profile = await getMe();
          setUserProfile(profile);
        } catch (e) {
          console.error("Failed to fetch user profile", e);
        }
      }
    };

    initialize();

    // Opened from the dashboard: rebuild the project from what the server
    // saved, rather than starting a blank workflow. This is what makes a
    // project resumable on another day or another device.
    const projectId = params.get('project');

    const resumeProject = async (id) => {
      setIsResuming(true);
      setResumeError(null);
      try {
        const { project } = await getMyProject(id);
        const state = project.workflow_state || {};

        setResumedProjectId(project.session_id);
        setSessionId(project.session_id);

        if (state.rawIntake ?? project.raw_request) setRawIntake(state.rawIntake ?? project.raw_request);
        if (state.intakeContract) setIntakeContract(state.intakeContract);
        if (state.intentSummary ?? project.intent_summary) {
          setIntentSummary(state.intentSummary ?? project.intent_summary);
        }
        if (state.microMapResult) setMicroMapResult(state.microMapResult);
        if (state.mapText) {
          setMapText(state.mapText);
          setMapGenerated(Boolean(state.mapGenerated ?? true));
        }
        if (state.approvedLockedJson) {
          setApprovedLockedJson(state.approvedLockedJson);
          setApproved(true);
        }
        if (state.blueprintRecord) setBlueprintRecord(state.blueprintRecord);
        if (state.generated) setGenerated(state.generated);
        if (Array.isArray(state.history)) setHistory(state.history);
        if (state.lastAnalyzedText) setLastAnalyzedText(state.lastAnalyzedText);
        if (state.validatedText) setValidatedText(state.validatedText);
      } catch (err) {
        console.error("Failed to resume project", err);
        setResumeError(
          "We could not open that project. It may have been deleted, or it belongs to another account."
        );
      } finally {
        setIsResuming(false);
      }
    };

    if (projectId) {
      resumeProject(projectId);
    } else if (!payment) {
      clearAllLocalWorkflow();
    } else {
      const draft = loadDraftMap();
      if (draft) setMapText(draft);
      const snap = loadApprovedSnapshot();
      if (snap?.jsonText) {
        setApprovedLockedJson(snap.jsonText);
        setApproved(true);
        if (snap.blueprintRecord) setBlueprintRecord(snap.blueprintRecord);
      }
      const gen = loadGeneratedCode();
      if (gen?.code) setGenerated(gen);
      setHistory(loadValidationHistory());
    }

    // Fetch dynamic rules from backend
    const fetchRules = async () => {
      try {
        const [arRules, detRules] = await Promise.all([
          getArchitecturalRules(),
          getDeterministicRules(),
        ]);
        setArchitecturalRules(arRules);
        setDeterministicRules(detRules);
      } catch (err) {
        console.error("Failed to fetch rules from backend:", err);
      }
    };
    fetchRules();
  }, []);

  // --- Autosave, so a project can be picked up again later ------------------
  //
  // Everything needed to resume lives on the server, not in this browser. The
  // save is debounced because these values change on every keystroke of the
  // intake box, and it is skipped for guests (who have no project to save to)
  // and while a resume is still loading (which would save a half-built state
  // over the good one).
  const resumeSnapshot = useMemo(
    () => ({
      rawIntake,
      intakeContract,
      intentSummary,
      microMapResult,
      mapText,
      mapGenerated,
      approved,
      approvedLockedJson,
      blueprintRecord,
      generated,
      history,
      lastAnalyzedText,
      validatedText,
      savedAt: new Date().toISOString(),
    }),
    [
      rawIntake, intakeContract, intentSummary, microMapResult, mapText, mapGenerated,
      approved, approvedLockedJson, blueprintRecord, generated, history,
      lastAnalyzedText, validatedText,
    ],
  );

  useEffect(() => {
    if (!isAuthenticated() || !sessionId || isResuming) return;
    // Nothing worth saving yet — don't create empty revisions.
    if (!rawIntake && !mapText && !generated) return;

    const timer = setTimeout(async () => {
      setSaveState("saving");
      try {
        await saveMyProjectState(sessionId, resumeSnapshot);
        setSaveState("saved");
      } catch (err) {
        // 404 just means the project row does not exist yet (a brand-new
        // session before the first analyze call persists it). Nothing to warn
        // the user about; the next save will land.
        if (err?.response?.status === 404) {
          setSaveState("idle");
          return;
        }
        console.error("Could not save project state", err);
        setSaveState("error");
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [sessionId, isResuming, resumeSnapshot, rawIntake, mapText, generated]);

  const handleCheckout = async (productType) => {
    if (!userProfile) {
      navigate('/build/login');
      return;
    }
    setIsRedirectingToStripe(true);
    try {
      let mapId = null;
      if (approvedLockedJson) {
        try {
          const m = JSON.parse(approvedLockedJson);
          mapId = m.map_id || null;
        } catch (e) {
          console.warn("Could not parse approved map for mapId");
        }
      }
      const res = await createCheckoutSession(userProfile.email, productType, mapId);
      window.location.href = res.url;
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Failed to initiate checkout");
      setIsRedirectingToStripe(false);
    }
  };

  const handleIntakeSubmit = async () => {
    if (!rawIntake.trim()) return;
    // Prevent duplicate submissions (double-click) and accidental re-runs of
    // an already-analyzed request.
    if (intakeInFlightRef.current || isIntaking) return;
    if (lastAnalyzedText === rawIntake) return;

    // Prevent intent summary generation if the user has no credits/trials left
    const isGuest = !isAuthenticated();
    if (isGuest) {
      const guestCanGen = getGuestTrialStatus().canGenerate;
      if (!guestCanGen) {
        setIntakeError("You have reached your limit of 3 free blueprints. Please sign in to continue.");
        return;
      }
    } else if (userProfile) {
      const hasFree = (userProfile.free_blueprint_attempts || 0) > 0;
      const hasCreds = (userProfile.build_credits || 0) > 0;
      if (!hasFree && !hasCreds) {
        setIntakeError("You do not have enough build credits to create a blueprint.");
        return;
      }
    }

    intakeInFlightRef.current = true;
    setIsIntaking(true);
    setIntakeError(null);
    intelProgress.begin("Preparing your request…");

    // Create a unique session ID for each new intake query
    const newSessionId = crypto.randomUUID ? crypto.randomUUID() : `session_${Date.now()}`;
    setSessionId(newSessionId);
    intelProgress.completeStage(0, "Analyzing your requirements…");

    try {
      // Each stage checkmark is set only when its request actually resolves.
      const [valRes, summaryRes, microMapRes] = await Promise.allSettled([
        validateIntake(rawIntake, null, newSessionId).then((r) => {
          intelProgress.completeStage(1);
          return r;
        }),
        analyzeIntent(rawIntake, newSessionId).then((r) => {
          intelProgress.completeStage(2, "Building Intelligence Report…");
          return r;
        }),
        generateMicroIntelligenceMap([{ signal_name: "intake_signal", evidence: { clause: rawIntake }, confidence_score: 0.9 }]).then((r) => {
          intelProgress.completeStage(3);
          return r;
        })
      ]);

      if (valRes.status === "fulfilled") {
        const res = valRes.value;
        const contract = res.contract || res;
        setIntakeContract(contract);
      } else {
        throw new Error(valRes.reason?.message || "Intake validation failed");
      }

      if (summaryRes.status === "fulfilled") {
        setIntentSummary(summaryRes.value.intent_summary || null);
      } else {
        setIntentSummary(null);
      }

      if (microMapRes.status === "fulfilled") {
        setMicroMapResult(microMapRes.value);
        if (summaryRes.status === "fulfilled") {
          // Both succeeded, let's update the session with the micro map
          try {
            await updateSessionMicroMap(newSessionId, microMapRes.value);
          } catch (e) {
            console.error("Failed to update session with micro map", e);
          }
        }
      } else {
        setMicroMapResult(null);
      }

      const summary = summaryRes.status === "fulfilled"
        ? summaryRes.value.intent_summary || null
        : null;
      if (summary) {
        // Step completed: remember what was analyzed so the button stays
        // disabled, reset downstream completion, and advance the user.
        setLastAnalyzedText(rawIntake);
        setMapGenerated(false);
        setValidatedText(null);
        intelProgress.finish("Intelligence Report complete.");
        if (summary.is_missing_information) {
          setTimeout(() => answerInputRef.current?.focus?.(), 400);
        } else {
          advanceTo(generateAreaRef, "center");
        }
      } else {
        intelProgress.fail("Intent analysis did not complete — please retry.");
      }
    } catch (err) {
      intelProgress.fail("Generation stopped — see the error below and retry.");
      setIntakeError("Intake validation failed: " + err.message);
    } finally {
      intakeInFlightRef.current = false;
      setIsIntaking(false);
    }
  };

  const handleAnswerSubmit = async () => {
    if (!answerInput.trim() || !intentSummary?.clarifying_questions?.length)
      return;

    if (intakeInFlightRef.current || isIntaking) return;

    const currentQuestion = intentSummary.clarifying_questions[0];
    const newContext = `${rawIntake}\n\nSystem Question: ${currentQuestion}\nUser Answer: ${answerInput.trim()}`;

    setRawIntake(newContext);
    setAnswerInput("");
    intakeInFlightRef.current = true;
    setIsIntaking(true);
    setIntakeError(null);
    intelProgress.begin("Refining the Intelligence Report with your answer…");
    intelProgress.completeStage(0, "Analyzing your requirements…");

    try {
      const [valRes, summaryRes, microMapRes] = await Promise.allSettled([
        validateIntake(newContext, null, sessionId).then((r) => {
          intelProgress.completeStage(1);
          return r;
        }),
        analyzeIntent(newContext, sessionId).then((r) => {
          intelProgress.completeStage(2, "Building Intelligence Report…");
          return r;
        }),
        generateMicroIntelligenceMap([{ signal_name: "intake_signal", evidence: { clause: newContext }, confidence_score: 0.95 }]).then((r) => {
          intelProgress.completeStage(3);
          return r;
        })
      ]);

      if (valRes.status === "fulfilled") {
        const res = valRes.value;
        const contract = res.contract || res;
        setIntakeContract(contract);
      } else {
        throw new Error(valRes.reason?.message || "Intake validation failed");
      }

      if (summaryRes.status === "fulfilled") {
        setIntentSummary(summaryRes.value.intent_summary || null);
      } else {
        setIntentSummary(null);
      }

      if (microMapRes.status === "fulfilled") {
        setMicroMapResult(microMapRes.value);
        if (summaryRes.status === "fulfilled") {
          try {
            await updateSessionMicroMap(sessionId, microMapRes.value);
          } catch (e) {
            console.error("Failed to update session with micro map", e);
          }
        }
      } else {
        setMicroMapResult(null);
      }

      const summary = summaryRes.status === "fulfilled"
        ? summaryRes.value.intent_summary || null
        : null;
      if (summary) {
        setLastAnalyzedText(newContext);
        setMapGenerated(false);
        setValidatedText(null);
        intelProgress.finish("Intelligence Report complete.");
        if (summary.is_missing_information) {
          setTimeout(() => answerInputRef.current?.focus?.(), 400);
        } else {
          advanceTo(generateAreaRef, "center");
        }
      } else {
        intelProgress.fail("Intent analysis did not complete — please retry.");
      }
    } catch (err) {
      intelProgress.fail("Refinement stopped — see the error below and retry.");
      setIntakeError("Intent refinement failed: " + err.message);
    } finally {
      intakeInFlightRef.current = false;
      setIsIntaking(false);
    }
  };

  const simulateLiveStreaming = async (fullText) => {
    let currentText = "";
    const chunkSize = 50;
    for (let i = 0; i < fullText.length; i += chunkSize) {
      currentText += fullText.slice(i, i + chunkSize);
      setMapText(currentText);
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
    setMapText(fullText);
  };

  const handleGenerateMap = async () => {
    // Prevent double-clicks and accidental re-generation of a completed map.
    if (generateInFlightRef.current || isGeneratingMap || mapGenerated) return;
    if (!isAuthenticated()) {
      const trialStatus = getGuestTrialStatus();
      if (!trialStatus.canGenerate) {
        setIntakeError("You have reached your limit of 3 free blueprints in 30 days. Please log in or wait for the limit to reset.");
        return;
      }
    }

    generateInFlightRef.current = true;
    setIsGeneratingMap(true);
    setMapText(""); // Clear previous text for streaming effect
    mapProgress.begin("Preparing the blueprint request…");
    try {
      const norm =
        intakeContract?.trace?.claude_normalized_request || rawIntake;
      mapProgress.completeStage(0, "Generating the flow map…");
      // Since the backend dataset is instant, we fetch the full map and simulate streaming on the frontend
      const response = await generateMapFromIntent(norm, intentSummary);
      const fullMapStr = response.map_json;
      mapProgress.completeStage(1, "Rendering the blueprint JSON…");

      if (!isAuthenticated()) {
        recordGuestTrialAttempt();
      }

      await simulateLiveStreaming(fullMapStr);

      setMapGenerated(true);
      setValidatedText(null);
      mapProgress.finish("Blueprint generated.");
      setResult(null);
      setNotification({
        type: "success",
        message: "Canonical Map generated successfully!",
      });
      setTimeout(() => setNotification(null), 5000);
      advanceTo(validatorSectionRef);

      // Update user profile locally to reflect used credits/trials if possible,
      // or just re-fetch user profile
      if (isAuthenticated()) {
        const profile = await getMe();
        setUserProfile(profile);
      }

    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      mapProgress.fail("Map generation failed — see the error below and retry.");
      if (err.response?.status === 402) {
        setIntakeError("Payment Required: " + errorMsg + ". Please purchase a Build Credit Pack.");
      } else {
        setIntakeError("Map generation failed: " + errorMsg);
      }
    } finally {
      generateInFlightRef.current = false;
      setIsGeneratingMap(false);
    }
  };

  const parsedMapMeta = useMemo(() => {
    try {
      const m = JSON.parse(mapText);
      return {
        map_id: m.map_id,
        version: m.version,
        project_name: m.project_name,
      };
    } catch {
      return null;
    }
  }, [mapText]);

  const runValidation = useCallback(async () => {
    setParseError(null);
    setApproved(false);
    setApprovedLockedJson(null);
    setBlueprintRecord(null);
    setGenerated(null);
    const initialResult = validateMapLocal(
      mapText,
      architecturalRules,
      deterministicRules,
    );
    setResult(initialResult);
    let parsedMap;
    try {
      parsedMap = JSON.parse(mapText);
    } catch (e) {
      setParseError(e.message);
      setResult(null);
      return;
    }

    setIsValidating(true);
    try {
      // Use strict validation (blocker + critical severity only) for fast, actionable feedback
      const r = await validateMapStrict(parsedMap);
      const criticalCount = (r.issues || []).filter(
        (i) => i.severity === "critical",
      ).length;

      const mappedIssues = (r.issues || []).map((i, idx) => ({
        issue_id: `server_issue_${idx}`,
        rule_type: i.type,
        rule_id: i.rule_id,
        severity: i.severity,
        location_type: i.node_id
          ? "node"
          : i.transition_id
            ? "transition"
            : "global",
        location_id: i.node_id || i.transition_id || "",
        message: i.message,
        node_id: i.node_id,
        transition_id: i.transition_id,
        why_it_matters: i.why_it_matters,
        suggested_fix: i.suggested_fix,
      }));

      const uiResult = {
        overall_status: r.status === "pass" ? "pass" : "fail",
        critical_count: criticalCount,
        map_id: parsedMap.map_id,
        version: parsedMap.version,
        validation_run_id: r.validation_token || `run_${Date.now()}`,
        issues: mappedIssues,
      };

      setResult(uiResult);
      const h = appendValidationHistory({
        overall_status: uiResult.overall_status,
        critical: uiResult.critical_count,
        map_id: uiResult.map_id,
        version: uiResult.version,
      });
      setHistory(h);
      if (uiResult.overall_status === "pass") {
        // Step completed: keep the validation button disabled for this exact
        // JSON and move the user on to the approval step.
        setValidatedText(mapText);
        advanceTo(approvalSectionRef);
      } else {
        setValidatedText(null);
      }
    } catch (err) {
      setParseError("API call failed: " + err.message);
      setResult(null);
    } finally {
      setIsValidating(false);
    }
  }, [mapText]);

  const canApprove = useMemo(() => {
    if (!result) return false;
    return (
      result.overall_status === "pass" ||
      result.overall_status === "pass_with_warnings"
    );
  }, [result]);

  const handleApprove = async () => {
    if (!canApprove || approved) return;
    setApproved(true);
    setApprovedLockedJson(mapText);

    // Persist the approval server-side so the Final Blueprint Outputs can be
    // generated from a versioned, stored blueprint. Guests (or a failed call)
    // fall back to client-side approval; outputs then use the preview API.
    let record = null;
    if (isAuthenticated() && result?.validation_run_id) {
      try {
        const parsedMap = JSON.parse(mapText);
        const res = await approveMapOnServer(parsedMap, result.validation_run_id, sessionId || null);
        if (res?.blueprint_id) {
          record = {
            blueprintId: res.blueprint_id,
            mapId: res.map_id,
            version: res.version,
            projectId: res.project_id || sessionId || null,
            requirementCount: res.requirement_count || 0,
          };
        }
      } catch (err) {
        // Client-side approval remains the source for output preview.
        console.warn("Server-side approval unavailable:", err?.message);
      }
    }
    setBlueprintRecord(record);

    saveApprovedSnapshot({
      jsonText: mapText,
      lockedAt: new Date().toISOString(),
      validationRunId: result?.validation_run_id,
      blueprintRecord: record,
    });

    // Step completed: move the user on to the Final Blueprint Outputs.
    advanceTo(outputsSectionRef);
  };

  const handleSaveDraft = () => {
    saveDraftMap(mapText);
  };

  const handleLoadDraft = () => {
    const d = loadDraftMap();
    if (d) {
      setMapText(d);
      setResult(null);
      setApproved(false);
      setApprovedLockedJson(null);
      setBlueprintRecord(null);
      setGenerated(null);
      setMonitoring(null);
      setMapGenerated(false);
      setValidatedText(null);
      mapProgress.reset();
    }
  };

  const handleGenerateCode = async () => {
    // 1. Determine the source. Priority: Approved Map > Intent Summary
    const isMapSource = !!approvedLockedJson;
    const sourceData = isMapSource ? approvedLockedJson : intentSummary;

    if (!sourceData) {
      setGenerated({
        error: "No intent summary or approved map found to generate code.",
      });
      return;
    }

    if (!isAuthenticated() && !isMapSource) {
      const trialStatus = getGuestTrialStatus();
      if (!trialStatus.canGenerate) {
        setGenerated({
          error: "You have reached your limit of 3 free blueprints in 30 days. Please log in to continue.",
        });
        return;
      }
    }

    let payload;
    try {
      // Intent Summary is an object in state, Approved Map is a JSON string
      payload =
        isMapSource && typeof sourceData === "string"
          ? JSON.parse(sourceData)
          : sourceData;
    } catch (e) {
      setGenerated({ error: "Invalid approved map JSON" });
      return;
    }

    setIsGenerating(true);
    setGenerated(null);

    try {
      setGenerated({
        code: "",
        status: "generating",
        artifactId: `art_${Date.now()}`,
      });

      // 2. Call the appropriate API (Standard Map Flow vs Shortcut Intent Flow)
      let streamBody;
      if (isMapSource) {
        streamBody = await streamCode(payload);
      } else {
        if (!isAuthenticated()) {
          recordGuestTrialAttempt();
        }
        streamBody = await streamCodeFromIntent(payload);
      }

      const reader = streamBody.getReader();
      const decoder = new TextDecoder();
      let fullCode = "";
      let chunkCount = 0;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        fullCode += chunk;
        chunkCount++;

        setGenerated((prev) => ({
          ...prev,
          code: fullCode,
          status: "success",
        }));

        // 3. Monitor live (only for Map-based flows, as monitor needs node structure)
        if (isMapSource && chunkCount % 3 === 0) {
          const liveRep = runLiveMonitor(
            fullCode,
            payload,
            "analyzing...",
            true,
            architecturalRules,
            deterministicRules,
          );
          setMonitoring(liveRep);
        }
      }

      const finalCleanCode = fullCode
        .replace(/```(?:\w+)?\s*([\s\S]*?)```/g, "$1")
        .trim();

      const finalPayload = {
        code: finalCleanCode,
        artifactId: `art_${Date.now()}`,
        mapFingerprint: isMapSource
          ? `fp_map_${Date.now()}`
          : `fp_intent_${Date.now()}`,
        generatedAt: new Date().toLocaleString(),
        status: "success",
      };
      setGenerated(finalPayload);
      saveGeneratedCode(finalPayload);
    } catch (err) {
      setGenerated({ error: "Code generation failed: " + err.message });
    } finally {
      setIsGenerating(false);
    }
  };

  const parsedCode = useMemo(() => {
    if (!generated?.code) return { fallback: "", frontend: "", backend: "" };
    const code = generated.code;

    const frontendRegex = /={3,}\s*FRONTEND\s*={3,}/i;
    const backendRegex = /={3,}\s*BACKEND\s*={3,}/i;

    const hasFrontend = frontendRegex.test(code);
    const hasBackend = backendRegex.test(code);

    if (!hasFrontend && !hasBackend) {
      return { fallback: code.trim(), frontend: "", backend: "" };
    }

    let frontend = "";
    let backend = "";

    if (hasFrontend) {
      const parts = code.split(frontendRegex);
      const afterFrontend = parts[1] || "";
      if (hasBackend && backendRegex.test(afterFrontend)) {
        frontend = afterFrontend.split(backendRegex)[0].trim();
      } else {
        frontend = afterFrontend.trim();
      }
    }

    if (hasBackend) {
      const parts = code.split(backendRegex);
      const afterBackend = parts[1] || "";
      if (hasFrontend && frontendRegex.test(afterBackend)) {
        backend = afterBackend.split(frontendRegex)[0].trim();
      } else {
        backend = afterBackend.trim();
      }
    }

    // Fallback if split fails for some reason
    if (!frontend && !backend) {
      return { fallback: code.trim(), frontend: "", backend: "" };
    }

    return { fallback: "", frontend, backend };
  }, [generated?.code]);

  const displayedCode = useMemo(() => {
    if (!generated?.code) return "";
    if (viewMode === "all") return generated.code.trim();
    if (viewMode === "frontend")
      return parsedCode.frontend || "No frontend code found.";
    if (viewMode === "backend")
      return parsedCode.backend || "No backend code found.";
    return generated.code.trim();
  }, [generated?.code, viewMode, parsedCode]);

  const parseGeneratedFiles = (rawText) => {
    if (!rawText) return { files: [], summary: null };

    let mainCode = rawText;
    let summary = null;

    const summaryIndex = rawText.lastIndexOf("### SUMMARY:");
    if (summaryIndex !== -1) {
      summary = rawText.substring(summaryIndex + 12).trim();
      mainCode = rawText.substring(0, summaryIndex);
    }

    const fileChunks = mainCode.split(/### FILE:\s*/).filter(Boolean);
    const files = [];

    fileChunks.forEach((chunk) => {
      const lines = chunk.split("\n");
      const filename = lines[0].trim();

      let content = chunk.substring(lines[0].length).trim();

      if (content.startsWith("```")) {
        const firstNewLine = content.indexOf("\n");
        if (firstNewLine !== -1) {
          content = content.substring(firstNewLine + 1);
        }
        if (content.endsWith("```")) {
          content = content.substring(0, content.length - 3);
        }
      }

      if (filename) {
        files.push({ filename, content: content.trim() });
      }
    });

    // Fallback if the AI hasn't output the marker yet
    if (files.length === 0 && mainCode.trim().length > 0) {
      files.push({ filename: "generating...", content: mainCode });
    }

    return { files, summary };
  };

  const { files: parsedFiles, summary: generatedSummary } = useMemo(
    () => parseGeneratedFiles(generated?.code || ""),
    [generated?.code],
  );

  const sandpackProps = useMemo(() => {
    if (!parsedFiles || parsedFiles.length === 0) return null;
    const files = {};
    let hasReact = false;
    parsedFiles.forEach((f) => {
      let name = f.filename;
      if (!name.startsWith("/")) name = "/" + name;
      files[name] = f.content;
      if (
        name.endsWith(".jsx") ||
        name.endsWith(".tsx") ||
        name.includes("React") ||
        name.endsWith("App.js")
      )
        hasReact = true;
    });
    return { files, template: hasReact ? "react" : "vanilla" };
  }, [parsedFiles]);

  useEffect(() => {
    if (parsedFiles.length > 0 && selectedFileTab >= parsedFiles.length) {
      setSelectedFileTab(parsedFiles.length - 1);
    }
  }, [parsedFiles.length, selectedFileTab]);

  const handleCopyCode = () => {
    const currentCode =
      parsedFiles[selectedFileTab < parsedFiles.length ? selectedFileTab : 0]
        ?.content;
    if (!currentCode) return;
    navigator.clipboard.writeText(currentCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadZip = async () => {
    if (!parsedFiles || parsedFiles.length === 0) return;
    setIsZipping(true);
    try {
      const zip = new JSZip();
      parsedFiles.forEach((file) => {
        if (file.filename && file.content) {
          zip.file(file.filename, file.content);
        }
      });
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = "generated-code.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error creating zip", err);
    } finally {
      setIsZipping(false);
    }
  };

  // Step 1 - Describe is complete only once a whole Flow Map exists: either
  // this session generated one, or a finished draft was restored. mapText alone
  // is not enough because it fills in chunks while the map streams.
  const step1Complete = useMemo(
    () => mapGenerated || (!isGeneratingMap && Boolean(mapText?.trim())),
    [mapGenerated, isGeneratingMap, mapText],
  );

  const correctionCount = history.length;
  const validationBuckets = useMemo(
    () => summarizeValidationBuckets(result?.issues || []),
    [result],
  );

  const workflowStep = useMemo(() => {
    if (isExported) return 6;
    if (generated?.code) return 5;
    if (approved && approvedLockedJson) return 5;
    if (result && result.overall_status === "pass" && !approved) return 3;
    if (result && result.overall_status === "fail") return 2;
    if (step1Complete) return 1;
    return 0;
  }, [isExported, generated, approved, approvedLockedJson, result, step1Complete]);

  // Work in flight on the current step.
  const activityBusy = isIntaking || isGeneratingMap || isValidating;

  // Step 1 is the only step whose owed action varies, so it is the only one
  // that overrides the shared per-step default.
  const activityNextAction = useMemo(() => {
    if (isIntaking) return "Analyzing your description…";
    if (isGeneratingMap) return "Generating Flow Map…";
    if (isValidating) return "Running validation…";
    if (workflowStep !== 0) return undefined;
    if (!intentSummary) return "Build Blueprint";
    if (intentSummary.is_missing_information)
      return "Answer the clarifying questions";
    return "Generate Flow Map";
  }, [isIntaking, isGeneratingMap, isValidating, workflowStep, intentSummary]);

  useEffect(() => {
    setActiveStep(workflowStep);
  }, [workflowStep, setActiveStep]);

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto", pb: 6 }}>
      {/* Resuming a saved project: tell the user what is happening, and say
          plainly when their work is being kept. */}
      {isResuming && (
        <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2 }}>
          Opening your saved project…
        </Alert>
      )}
      {resumeError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setResumeError(null)}>
          {resumeError}
        </Alert>
      )}
      {resumedProjectId && !isResuming && !resumeError && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={() => navigate("/build/dashboard")}>
              My Dashboard
            </Button>
          }
        >
          Continuing a saved project.{" "}
          {saveState === "saving" && "Saving…"}
          {saveState === "saved" && "Your changes are saved automatically."}
          {saveState === "error" && "We could not save your latest changes."}
        </Alert>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={paymentStatus === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {paymentStatus === 'success'
            ? 'Payment successful! Your credits/subscription have been updated.'
            : 'Payment was failed. Please try again.'}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: { xs: "center", md: "space-between" },
          gap: { xs: 2, md: 0 },
          width: "100%",
          mb: 3,
        }}
      >
        <Box className="logo-card hidden">
          <img
            src="/one-pass-logo.png" // Replace with your logo path
            alt="OnePass Logo"
            className="logo-image"
          />

          <Box className="logo-content">
            <Typography
              variant="h4"
              component="h1"
              fontWeight={700}
              color="primary.dark"
            >
              OnePass
            </Typography>

            <Typography
              variant="body2"
              fontWeight={600}
              color="rgb(100 116 139)"
              textTransform="uppercase"
            >
              Powered by OnePassBlueprint.com
            </Typography>
          </Box>
        </Box>
        <Stack
          spacing={0.5}
          sx={{
            // mb: 3,
            pt: 1,
            pb: 1,
          }}
        >
          <Typography
            fontWeight={800}
            color="primary.main"
            sx={{
              fontSize: { xs: "1.875rem", sm: "2.25rem" },
              lineHeight: { xs: "2.25rem", sm: "2.5rem" },
              letterSpacing: "-0.025em",
              textAlign: { xs: "center", md: "left" },
            }}
          >
            Fix it before you build it.
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: { xs: "center", md: "left" } }}
          >
            Turn ideas, requirements, and specs into validated build blueprints
            using proprietary blueprint intelligence.
          </Typography>
        </Stack>
      </Box>
      <ActivityPanel
        workflowStep={workflowStep}
        nextAction={activityNextAction}
        busy={activityBusy}
      >
        <Stack spacing={3}>
          {/* 0 — Natural Language Intake & Meaning Validation */}
          <Paper
            elevation={0}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              p: 3,
              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)",
              border: isDragging ? "2px dashed #0055FF" : "2px dashed transparent",
              bgcolor: isDragging ? "rgba(0, 85, 255, 0.04)" : "background.paper",
              transition: "all 0.2s ease"
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} >
              {/* <GavelIcon color="primary" /> */}
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1.5 }}>
                1. Describe what you want built
              </Typography>
              {step1Complete && (
                <Chip
                  label="Step 1 – Describe Complete"
                  color="success"
                  size="small"
                  icon={<CheckCircleOutlineIcon />}
                  sx={{ mt: 1.5, fontWeight: 600 }}
                />
              )}
            </Stack>
            <Box sx={{ mb: 2 }}>
              {/* <Typography variant="h6" fontWeight={700} color="text.primary">
              Describe what you want built
            </Typography> */}
              <Typography variant="body2" color="text.secondary" >
                Type your idea, paste a requirement, or drop in a full spec.
                OnePass will turn it into a structured blueprint.
              </Typography>
            </Box>

            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                label="Describe what you want built"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder="Paste your app idea, feature request, workflow, system requirement, or implementation spec here…"
                value={rawIntake}
                onChange={(e) => setRawIntake(e.target.value)}
              />

              <input
                type="file"
                accept=".txt,.md,.pdf,.docx"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileUpload}
              />

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    borderRadius: "9999px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    borderColor: "divider",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: "action.hover",
                      borderColor: "divider",
                    }
                  }}
                >
                  Upload Document
                </Button>
                <Button
                  variant="contained"
                  onClick={handleIntakeSubmit}
                  disabled={isIntaking || !rawIntake || lastAnalyzedText === rawIntake}
                  sx={{
                    minWidth: 180,
                    px: 2.5,
                    py: 1.25,
                    borderRadius: "12px",
                    background:
                      "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(0,85,255,.35)",
                    transition:
                      "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s",
                    "&:hover": {
                      background:
                        "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
                      transform: "translateY(-2px) scale(1.02)",
                      boxShadow: "0 14px 40px rgba(0,85,255,.5)",
                    },
                    "&:active": {
                      transform: "translateY(0) scale(.98)",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.6,
                      background: "rgba(0, 0, 0, 0.12)",
                      color: "rgba(0, 0, 0, 0.40)",
                    },
                  }}
                >
                  {isIntaking
                    ? "Validating..."
                    : lastAnalyzedText === rawIntake && intentSummary && !intentSummary.is_missing_information
                      ? "✓ Analysis Completed"
                      : lastAnalyzedText === rawIntake
                        ? "✓ Analyzed — answer below"
                        : "Build Blueprint"}
                </Button>
                {/* <Button
                  variant="contained"
                  onClick={handleIntakeSubmit}
                  disabled={isIntaking || !rawIntake}
                  sx={{ minWidth: 160 }}
                >
                  {isIntaking ? "Validating..." : "Build Blueprint"}
                </Button> */}
              </Box>
            </Stack>

            {/* The "Intelligence Report Generation" staged progress panel was
                removed from this step; the button label ("Validating...") and
                the result panel below convey the state. The intelProgress
                controller is still driven so the flow logic is unchanged. */}

            {/* Inline notification removed from here as it's now in the map section */}

            {intakeError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {intakeError}
              </Alert>
            )}

            {intakeContract && intakeContract.readiness === "ready" && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                {/* <Alert severity="success">
                  Meaning validated successfully! Request is ready for mapping and
                  implementation.
                </Alert> */}

                {intentSummary && (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderColor: "primary.main", bgcolor: "#f8f9ff" }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 2 }}
                    >
                      <AutoAwesomeIcon color="primary" fontSize="small" />
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        fontWeight={700}
                      >
                        Intent Summary (Simplified)
                      </Typography>
                    </Stack>

                    {intentSummary.is_missing_information ? (
                      <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Clarification Needed
                        </Typography>
                        {intentSummary.assistant_message && (
                          <Typography
                            variant="body2"
                            sx={{ mt: 1, mb: 2, whiteSpace: "pre-line" }}
                          >
                            {intentSummary.assistant_message}
                          </Typography>
                        )}
                        {understoodLabels(intentSummary).length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mb: 1, color: "text.secondary" }}
                          >
                            Already understood from your description:{" "}
                            {understoodLabels(intentSummary).join(", ")}. Everything else
                            is either inferred or covered by standard defaults — only the
                            question below needs your decision.
                          </Typography>
                        )}
                        {intentSummary.clarifying_questions &&
                          intentSummary.clarifying_questions.length > 0 ? (
                          <>
                            <Typography
                              variant="body1"
                              sx={{ mt: 1, fontWeight: 500 }}
                            >
                              {intentSummary.clarifying_questions[0]}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                              <TextField
                                size="small"
                                fullWidth
                                inputRef={answerInputRef}
                                placeholder="Type your answer here..."
                                value={answerInput}
                                onChange={(e) => setAnswerInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAnswerSubmit();
                                  }
                                }}
                              />
                              <Button
                                variant="contained"
                                onClick={handleAnswerSubmit}
                                disabled={!answerInput.trim() || isIntaking}
                              >
                                Submit
                              </Button>
                            </Stack>
                          </>
                        ) : (
                          <>
                            {!intentSummary.clarifying_questions &&
                              intentSummary.clarifying_question && (
                                <Typography variant="body2">
                                  {intentSummary.clarifying_question}
                                </Typography>
                              )}
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Please provide the missing information in the
                              Correction Loop below to generate the full intent
                              summary.
                            </Typography>
                          </>
                        )}
                      </Alert>
                    ) : (
                      <Stack spacing={2}>
                        {intentSummary.assistant_message && (
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: "#ffffff",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-line",
                                color: "text.secondary",
                              }}
                            >
                              {intentSummary.assistant_message}
                            </Typography>
                          </Box>
                        )}

                        {/* Business view — plain language, no internal labels. */}
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              textTransform: "uppercase",
                              fontWeight: 700,
                              letterSpacing: 0.5,
                            }}
                          >
                            What is being built
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, mt: 0.5 }}
                          >
                            {intentSummary.summary_view?.what_is_being_built ||
                              intentSummary.system_building ||
                              intentSummary.intent}
                          </Typography>
                        </Box>

                        {intentSummary.summary_view?.who_will_use_it && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}
                            >
                              Who will use it
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                              {intentSummary.summary_view.who_will_use_it}
                            </Typography>
                          </Box>
                        )}

                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              textTransform: "uppercase",
                              fontWeight: 700,
                              letterSpacing: 0.5,
                            }}
                          >
                            What problem it solves
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, mt: 0.5 }}
                          >
                            {intentSummary.summary_view?.problem_it_solves ||
                              intentSummary.intended_result ||
                              intentSummary.reasoning}
                          </Typography>
                        </Box>

                        {intentSummary.summary_view?.main_workflow?.length > 0 && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}
                            >
                              How the main workflow works
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                              {intentSummary.summary_view.main_workflow.join(" → ")}
                            </Typography>
                          </Box>
                        )}

                        {Array.isArray(intentSummary.assumptions) &&
                          intentSummary.assumptions.length > 0 && (
                            <Box data-testid="assumptions-list">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}
                              >
                                Assumptions / standard defaults
                              </Typography>
                              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                                {intentSummary.assumptions.map((a, i) => (
                                  <Typography component="li" variant="body2" key={i}>
                                    ✓ {a}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}

                        {Array.isArray(intentSummary.inferences) &&
                          intentSummary.inferences.length > 0 && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}
                              >
                                Understood from your description
                              </Typography>
                              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                                {intentSummary.inferences.map((a, i) => (
                                  <Typography component="li" variant="body2" key={i}>
                                    {a}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}

                        <Divider />

                        {/* Developer / expert view — technical classification and
                            the clarification decision trace, collapsed by default
                            so business users are not shown internal metadata. */}
                        <Accordion variant="outlined" disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              Developer details
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                        <Stack
                          direction="row"
                          spacing={3}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ rowGap: 2 }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700 }}
                            >
                              Technical Intent
                            </Typography>
                            <Typography variant="body2">
                              {intentSummary.intent || "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700 }}
                            >
                              Query Word Count
                            </Typography>
                            <Typography variant="body2">
                              {rawIntake ? rawIntake.trim().split(/\s+/).filter(w => w.length > 0).length : 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700 }}
                            >
                              Uploaded Pages
                            </Typography>
                            <Typography variant="body2">
                              {rawIntake ? Math.ceil(rawIntake.trim().split(/\s+/).filter(w => w.length > 0).length / 300) : 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700 }}
                            >
                              Pattern
                            </Typography>
                            <Typography variant="body2">
                              {intentSummary.pattern || "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700 }}
                            >
                              Behavior
                            </Typography>
                            <Typography variant="body2">
                              {intentSummary.behavior || "N/A"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700 }}
                            >
                              Complex Level
                            </Typography>
                            <Typography variant="body2">
                              {intentSummary.complex_level !== undefined ? `Level ${intentSummary.complex_level}` : "N/A"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700 }}
                            >
                              Model
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "success.main", fontWeight: 600 }}
                            >
                              {intentSummary.model_used ||
                                "Four-Layer Meaning Engine"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700 }}
                            >
                              Fallback Attempted
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: intentSummary.fallback_attempted ? "warning.main" : "text.primary", fontWeight: 600 }}
                            >
                              {intentSummary.fallback_attempted ? "True" : "False"}
                            </Typography>
                          </Box>
                          {intentSummary.fallback_attempted && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: "uppercase", fontWeight: 700 }}
                              >
                                Fallback Reason
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "error.main", fontWeight: 600 }}
                              >
                                {intentSummary.fallback_reason || "N/A"}
                              </Typography>
                            </Box>
                          )}
                        </Stack>

                        {Array.isArray(intentSummary.clarification_trace) &&
                          intentSummary.clarification_trace.length > 0 && (
                            <Box sx={{ mt: 2 }} data-testid="clarification-trace">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: "uppercase", fontWeight: 700, display: "block", mb: 1 }}
                              >
                                Clarification decision trace
                              </Typography>
                              <TableContainer sx={{ maxHeight: 320 }}>
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Requirement</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Source</TableCell>
                                      <TableCell>Decision</TableCell>
                                      <TableCell>Reason</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {intentSummary.clarification_trace.map((t, i) => (
                                      <TableRow key={`${t.requirement}-${i}`}>
                                        <TableCell>{t.label || t.requirement}</TableCell>
                                        <TableCell>{t.status}</TableCell>
                                        <TableCell>{t.source || "—"}</TableCell>
                                        <TableCell>
                                          <Chip size="small" label={t.decision} color={decisionColor(t.decision)} />
                                        </TableCell>
                                        <TableCell>{t.reason}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                          </AccordionDetails>
                        </Accordion>
                      </Stack>
                    )}

                    {intentSummary && !intentSummary.is_missing_information && (
                      <Box sx={{ mt: 4 }}>
                        <HCLLiteView sessionId={sessionId} tenantId="default-tenant" />
                      </Box>
                    )}

                    {microMapResult && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: "uppercase", fontWeight: 700, mb: 1, display: 'block' }}
                        >
                          Micro-Intelligence Map
                        </Typography>
                        <Box sx={{ p: 1.5, bgcolor: 'rgba(0, 85, 255, 0.04)', borderRadius: 1, border: '1px solid rgba(0, 85, 255, 0.1)' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.dark' }}>
                            <strong>Status:</strong> {microMapResult.status} <br />
                            <strong>Instance Map ID:</strong> {microMapResult.instance_map_id} <br />
                            {/*  <strong>Configured Nodes:</strong> {microMapResult.nodes?.length || 0} */}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                )}

                {intentSummary && !intentSummary.is_missing_information && (!mapText || mapGenerated || isGeneratingMap) && (
                  <Box ref={generateAreaRef}>
                    {(() => {
                      // Completed step: keep the button visible but disabled and
                      // gray so the finished state is obvious and cannot re-run.
                      if (mapGenerated) {
                        return (
                          <Button
                            variant="contained"
                            disabled
                            startIcon={<CheckCircleOutlineIcon />}
                            sx={{
                              width: "fit-content",
                              "&.Mui-disabled": {
                                bgcolor: "success.main",
                                color: "common.white",
                              },
                            }}
                          >
                            ✓ Flow Map Generated
                          </Button>
                        );
                      }
                      const complexity = intentSummary?.complexity_level || 1;
                      let req = 1;
                      if (complexity === 3) req = 2;
                      if (complexity >= 4) req = 5;

                      const isGuest = !isAuthenticated();
                      let guestCanGen = false;
                      if (isGuest) {
                        guestCanGen = getGuestTrialStatus().canGenerate;
                      }

                      const hasFree = (userProfile?.free_blueprint_attempts || 0) > 0 && complexity <= 3;
                      const hasCreds = (userProfile?.build_credits || 0) >= req;
                      const canGen = isGuest ? guestCanGen : (hasFree || hasCreds);

                      if (!canGen) {
                        if (isGuest) {
                          return (
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate('/build/login')}
                              >
                                Sign in to continue
                              </Button>
                              <Typography variant="body2" color="error">
                                You have reached your limit of 3 free blueprints.
                              </Typography>
                            </Stack>
                          );
                        }

                        return (
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleCheckout('credit_pack')}
                              disabled={isRedirectingToStripe}
                            >
                              {isRedirectingToStripe ? "Redirecting..." : "Buy Build Credits ($5 for 10)"}
                            </Button>
                            <Typography variant="body2" color="error">
                              Not enough credits (requires {req}).
                            </Typography>
                          </Stack>
                        );
                      }

                      const buttonText = hasFree ? 'Free Trial' : req + ' Credits';

                      return (
                        <Stack spacing={1.25}>
                          {!isGeneratingMap && (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "warning.dark" }}
                            >
                              Required: Generate the Flow Map to complete
                              Describe and unlock Step 2 – Validated Blueprint.
                            </Typography>
                          )}
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleGenerateMap}
                            disabled={isGeneratingMap}
                            startIcon={<AutoAwesomeIcon />}
                            sx={{ width: "fit-content" }}
                          >
                            {isGeneratingMap
                              ? "Generating Flow Map..."
                              : "Generate Flow Map & Complete Step 1"}
                          </Button>
                        </Stack>
                      );
                    })()}
                  </Box>
                )}

                {/* Staged progress for blueprint generation. */}
                <GenerationProgress
                  title="Blueprint Generation"
                  stages={MAP_GEN_STAGES}
                  controller={mapProgress}
                />
              </Stack>
            )}

            {intakeContract && intakeContract.readiness !== "ready" && (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderColor: "error.main", bgcolor: "#fffcfc" }}
              >
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Status: {intakeContract.readiness.toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Exclusions / Failures:</strong>{" "}
                  {intakeContract.trace?.support_checks_failed?.join(", ") ||
                    "None"}
                  <br />
                  <strong>Ambiguities:</strong>{" "}
                  {intakeContract.ambiguities?.join(", ") || "None"}
                </Typography>
              </Paper>
            )}
          </Paper>


          {/* 2 — Validator */}
          <Paper
            ref={validatorSectionRef}
            elevation={0}
            sx={{ p: 3, boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)" }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              {/* <GavelIcon color="primary" /> */}

              <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                2. Validated Blueprint
              </Typography>
              {!step1Complete && (
                <Chip
                  label="Locked until Step 1 is complete"
                  size="small"
                  icon={<LockOutlinedIcon />}
                  sx={{ mt: 1, fontWeight: 600 }}
                />
              )}
            </Stack>
            {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Runs three passes in order. Issues include gaps, conflicts, and rule violations with
            guidance.
          </Typography> */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Button
                variant="contained"
                onClick={runValidation}
                disabled={
                  isValidating ||
                  isGeneratingMap ||
                  !step1Complete ||
                  (result?.overall_status === "pass" && validatedText === mapText)
                }
                startIcon={
                  result?.overall_status === "pass" && validatedText === mapText
                    ? <CheckCircleOutlineIcon />
                    : <SecurityIcon />
                }
              >
                {isGeneratingMap
                  ? "Waiting for map..."
                  : isValidating
                    ? "Validating remotely..."
                    : result?.overall_status === "pass" && validatedText === mapText
                      ? "✓ Validation Passed"
                      : "Run validation"}
              </Button>
            </Stack>

            {!step1Complete && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: "warning.dark" }}>
                Locked: generate the Flow Map in Step 1 – Describe to unlock
                validation.
              </Typography>
            )}
            {!result && step1Complete && (
              <Typography variant="body2" color="text.secondary">
                Run validation to see simple totals: issues, gaps, and conflicts.
              </Typography>
            )}
            {result && (
              <Stack spacing={1.5}>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Chip
                    label={`Issues: ${validationBuckets.totalIssues}`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`Gaps: ${validationBuckets.gaps}`}
                    color="warning"
                    size="small"
                  />
                  <Chip
                    label={`Conflicts: ${validationBuckets.conflicts}`}
                    color="error"
                    size="small"
                  />
                </Stack>
                <Divider />
                <Typography variant="subtitle2">Validator output</Typography>
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Rule Type</TableCell>
                        <TableCell>Rule ID</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Explanation</TableCell>
                        <TableCell>Why it matters</TableCell>
                        <TableCell>Suggested fix direction</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.issues.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="success.main">
                              Pass.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        result.issues.map((i) => (
                          <TableRow key={i.issue_id} hover>
                            <TableCell sx={{ fontSize: 12 }}>
                              {i.rule_type}
                            </TableCell>
                            <TableCell
                              sx={{ fontFamily: "monospace", fontSize: 12 }}
                            >
                              {i.rule_id}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={i.severity}
                                size="small"
                                color={
                                  i.severity === "critical" ? "error" : "default"
                                }
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {i.node_id
                                ? `Node: ${i.node_id}`
                                : i.transition_id
                                  ? `Transition: ${i.transition_id}`
                                  : "Global"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, minWidth: 220 }}>
                              {i.message}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, minWidth: 200 }}>
                              {i.why_it_matters || "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, minWidth: 220 }}>
                              {i.suggested_fix || "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}
          </Paper>

          {/* 3 — Correction loop */}
          <Paper
            elevation={0}
            sx={{ p: 3, boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)" }}
          >
            <Typography variant="h6" fontWeight={700}>
              3. Correction loop
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edit the map JSON → run validation again until acceptable. The team
              resolves issues returned above; iteration count is tracked locally.
            </Typography>
            <Chip
              label={`Validation runs (local history): ${correctionCount}`}
              size="small"
              variant="outlined"
              sx={{ mt: 1, mb: 1, fontSize: 12 }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Total issues to fix: <strong>{result?.issues?.length || 0}</strong>
            </Typography>
            {result?.overall_status === "fail" && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Map not acceptable yet — fix critical/major issues, then
                re-validate.
              </Alert>
            )}
          </Paper>

          {/* 4 — Approval */}
          <Paper
            ref={approvalSectionRef}
            elevation={0}
            sx={{ p: 3, boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* <CheckCircleOutlineIcon color="success" /> */}
              <Typography variant="h6" fontWeight={700}>
                4. Approve — lock deterministic map as source of truth
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Only after validation passes policy. Locked snapshot is stored in
              the browser.
            </Typography>
            <Button
              variant="contained"
              color="success"
              disabled={!canApprove || approved}
              onClick={handleApprove}
              startIcon={<CheckCircleOutlineIcon />}
              sx={{ mb: 2 }}
            >
              {approved ? "Approved & locked" : "Approve & lock map"}
            </Button>
          </Paper>
          {/* Blueprint Summary */}
          {approved && approvedLockedJson && (
            <Box ref={outputsSectionRef}>

              {notification && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    border: "1.5px solid",
                    borderColor:
                      notification.type === "success" ? "#22c55e" : "#ef4444",
                    background:
                      notification.type === "success"
                        ? "linear-gradient(135deg, rgba(34,197,94,0.12), rgba(16,185,129,0.08))"
                        : "linear-gradient(135deg, rgba(239,68,68,0.12), rgba(249,115,22,0.08))",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 22,
                        bgcolor:
                          notification.type === "success"
                            ? "rgba(34,197,94,0.15)"
                            : "rgba(239,68,68,0.15)",
                        flexShrink: 0,
                      }}
                    >
                      {notification.type === "success" ? "✅" : "🛑"}
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        sx={{
                          color:
                            notification.type === "success" ? "#16a34a" : "#dc2626",
                          letterSpacing: 1,
                          textTransform: "uppercase",
                        }}
                      >
                        {notification.type === "success"
                          ? "Map Generated Successfully"
                          : "Error"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.3 }}
                      >
                        {notification.message}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    onClick={() => setNotification(null)}
                    sx={{ minWidth: "auto", ml: 1, color: "text.secondary" }}
                  >
                    ✕
                  </Button>
                </Box>
              )}

              {parseError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  JSON parse error: {parseError}
                </Alert>
              )}

              {/* Final Blueprint Outputs — four coordinated views generated
                  from the same final approved blueprint. The raw JSON lives
                  in the Developer Package tab. */}
              <Box sx={{ mt: 1 }}>
                <FinalBlueprintOutputs
                  mapText={approvedLockedJson}
                  blueprintId={blueprintRecord?.blueprintId || null}
                  blueprintVersion={blueprintRecord?.version || null}
                  sessionId={sessionId || null}
                  onExportOrCopy={() => setIsExported(true)}
                />
                {/* Second Pair of Eyes needs a saved (server-side) approval to
                    check against; guests approve client-side only. */}
                {blueprintRecord?.blueprintId && (
                  <SecondPairOfEyesCard
                    sessionId={blueprintRecord.projectId || sessionId || null}
                    blueprintVersion={blueprintRecord.version}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Expert Support Section */}
          <Paper
            elevation={0}
            sx={{ p: 3, boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)" }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              Need Expert Support?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Get access to more than 1,000 engineers and industry specialists across a broad range of technologies and project needs.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Whether you need implementation support, technical expertise, additional development, or a complete design-and-build solution, our team can help.
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setQuoteFormOpen(true)}>
              Request a Custom Quote
            </Button>
          </Paper>

          {/* Commented out Steps 5, 6, 7 */}
          {false && (
            <>
              <Paper
                elevation={0}
                sx={{ p: 3, boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)" }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  {/* <AutoAwesomeIcon color="primary" /> */}
                  <Typography variant="h6" fontWeight={700}>
                    5. Real-time Code generation (OnePass)
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" >
                  Code generation is powered by{" "}
                  <strong>
                    OnePassBlueprint.com
                    {/* (claude-sonnet-4-6) */}
                  </strong>
                  . Intent and map generation are powered by{" "}
                  <strong>Four-Layer Meaning Engine</strong>. Only the{" "}
                  <strong>approved</strong> canonical map is used as the source of
                  truth for code generation.
                </Typography>

                {!getToken() && (
                  <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
                    Please <strong>Sign In</strong> or <strong>Sign Up</strong> to unlock Real-time Code generation.
                  </Alert>
                )}

                <Button
                  variant="contained"
                  disabled={!getToken() || !approvedLockedJson || isGenerating}
                  onClick={handleGenerateCode}
                  startIcon={<AutoAwesomeIcon />}
                  sx={{ mt: 2, mb: 1, }}
                >
                  {isGenerating
                    ? "Generating code..."
                    : generated?.code
                      ? "Regenerate implementation"
                      : "Generate code from approved map"}
                </Button>
                {isGenerating && (
                  <Box sx={{ width: "100%", mt: 2 }}>
                    <LinearProgress />
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block" }}
                      color="text.secondary"
                    >
                      OnePass is analyzing your approved map and generating code...
                    </Typography>
                  </Box>
                )}
                {!approvedLockedJson && getToken() && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Approve a map first to enable generation.
                  </Typography>
                )}
                {generated?.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {generated.error}
                  </Alert>
                )}
                {generated && generated.code && (
                  <Box sx={{ mt: 2, position: "relative" }}>
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <ToggleButtonGroup
                        value={isLivePreview ? "preview" : "code"}
                        exclusive
                        onChange={(e, val) => {
                          if (val !== null) setIsLivePreview(val === "preview");
                        }}
                        size="small"
                      >
                        <ToggleButton value="code">Code View</ToggleButton>
                        {/* <ToggleButton value="preview">Live Preview</ToggleButton> */}
                      </ToggleButtonGroup>

                      {isLivePreview && (
                        <Typography variant="caption" color="text.secondary">
                          *Note: Backend code (like Python/FastAPI) cannot run in this
                          sandbox.
                        </Typography>
                      )}
                    </Box>

                    {!isLivePreview ? (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            bgcolor: "grey.200",
                            p: 0,
                            borderTopLeftRadius: 4,
                            borderTopRightRadius: 4,
                            border: 1,
                            borderColor: "divider",
                            borderBottom: 0,
                          }}
                        >
                          <Tabs
                            value={
                              selectedFileTab < parsedFiles.length
                                ? selectedFileTab
                                : 0
                            }
                            onChange={(e, val) => setSelectedFileTab(val)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                              minHeight: 48,
                              "& .MuiTab-root": {
                                textTransform: "none",
                                minHeight: 48,
                                fontWeight: 600,
                              },
                            }}
                          >
                            {parsedFiles.map((file, idx) => (
                              <Tab key={idx} label={file.filename} />
                            ))}
                          </Tabs>
                          <Box sx={{ pr: 1, display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ mr: 2 }}
                            >
                              Tokens: {generated.tokens_used}
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<CloudDownloadIcon fontSize="small" />}
                              onClick={handleDownloadZip}
                              disabled={isZipping || parsedFiles.length === 0}
                              sx={{ textTransform: "none", mr: 1 }}
                            >
                              {isZipping ? "Zipping..." : "Download ZIP"}
                            </Button>
                            <Button
                              size="small"
                              startIcon={
                                copied ? (
                                  <CheckIcon fontSize="small" />
                                ) : (
                                  <ContentCopyIcon fontSize="small" />
                                )
                              }
                              onClick={handleCopyCode}
                              sx={{ textTransform: "none" }}
                            >
                              {copied ? "Copied!" : "Copy code"}
                            </Button>
                          </Box>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={12}
                          maxRows={24}
                          value={
                            parsedFiles[
                              selectedFileTab < parsedFiles.length
                                ? selectedFileTab
                                : 0
                            ]?.content || ""
                          }
                          InputProps={{ readOnly: true }}
                          sx={{
                            "& .MuiInputBase-root": {
                              borderTopLeftRadius: 0,
                              borderTopRightRadius: 0,
                              backgroundColor: "grey.50",
                            },
                            "& .MuiInputBase-input": {
                              fontFamily: "ui-monospace, monospace",
                              fontSize: 12,
                            },
                          }}
                        />
                      </>
                    ) : (
                      <Box
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        {sandpackProps ? (
                          <Sandpack
                            template={sandpackProps.template}
                            files={sandpackProps.files}
                            theme="light"
                            options={{
                              showNavigator: true,
                              showTabs: true,
                              editorHeight: 600,
                            }}
                          />
                        ) : (
                          <Alert severity="warning">No files found to preview.</Alert>
                        )}
                      </Box>
                    )}
                    {generatedSummary && (
                      <Box sx={{ mt: 2 }}>
                        <Alert
                          severity="info"
                          variant="outlined"
                          sx={{ "& .MuiAlert-message": { width: "100%" } }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              mb: 1,
                              textTransform: "uppercase",
                              fontWeight: "bold",
                            }}
                          >
                            Developer Guidelines
                          </Typography>
                          <Box
                            sx={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}
                            dangerouslySetInnerHTML={{
                              __html: generatedSummary.replace(
                                /\*\*(.*?)\*\*/g,
                                "<strong>$1</strong>",
                              ),
                            }}
                          />
                        </Alert>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>

              {/* 6 — Monitoring */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)",
                  // bgcolor: monitoring ? "rgba(0,0,0,0.02)" : "transparent",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  {/* <BugReportIcon color="primary" /> */}
                  <Typography variant="h6" fontWeight={700}>
                    6. Monitor print
                  </Typography>
                  {isGenerating && (
                    <Chip
                      label="LIVE ANALYZING"
                      size="small"
                      color="secondary"
                      sx={{
                        animation: "pulse 1.5s infinite",
                        "@keyframes pulse": {
                          "0%": { opacity: 1, transform: "scale(1)" },
                          "50%": { opacity: 0.7, transform: "scale(0.95)" },
                          "100%": { opacity: 1, transform: "scale(1)" },
                        },
                      }}
                    />
                  )}
                </Stack>

                {!monitoring && (
                  <Typography variant="body2" color="text.secondary">
                    Start generation to see real-time monitoring reports.
                  </Typography>
                )}

                {monitoring && (
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2">
                        Overall Drift Status:
                      </Typography>
                      <Chip
                        color={
                          monitoring.overallStatus === "pass" ? "success" : "error"
                        }
                        label={
                          monitoring.overallStatus === "pass"
                            ? "STABLE"
                            : "DRIFT DETECTED"
                        }
                        size="small"
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Issues found: ${(monitoring.printPhase.issues?.filter((i) => i.severity !== "pass" && i.severity !== "informational").length || 0) + (monitoring.implementationPhase.issues?.filter((i) => i.severity !== "pass" && i.severity !== "informational").length || 0)}`}
                      />
                    </Stack>
                    {monitoring.overallStatus === "pass" && (
                      <Alert severity="success" sx={{ py: 0 }}>
                        Excellent: Implementation matches approved map contract 100%.
                      </Alert>
                    )}
                    <LinearProgress
                      variant="determinate"
                      value={100}
                      sx={{ mb: 1 }}
                    />
                    <MonitorTable
                      title="Monitoring issues"
                      rows={[
                        ...(monitoring.printPhase.issues || []),
                        ...(monitoring.implementationPhase.issues || []),
                      ]}
                    />
                  </Stack>
                )}
              </Paper>

              {/* 7 — Runtime Error Diagnostics */}
              <Paper
                elevation={0}
                sx={{ p: 3, boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)" }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {/* <BugReportIcon color="error" /> */}
                  <Typography variant="h6" fontWeight={700}>
                    7. Runtime Error Diagnostics
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  If you encounter any errors while running the generated project
                  locally, paste the error trace here to diagnose the issue.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Paste your runtime error or terminal output here..."
                  value={runError}
                  onChange={(e) => setRunError(e.target.value)}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 13,
                      bgcolor: "grey.50",
                    },
                  }}
                />
                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={!runError.trim() || isDiagnosing}
                    onClick={async () => {
                      if (!runError.trim()) return;
                      setIsDiagnosing(true);
                      setDiagnosticResult(null);
                      try {
                        const res = await diagnoseError(
                          runError,
                          generated?.code || "",
                        );
                        setDiagnosticResult(res);
                      } catch (err) {
                        setNotification({
                          type: "error",
                          message: "Failed to diagnose error.",
                        });
                      } finally {
                        setIsDiagnosing(false);
                      }
                    }}
                  >
                    {isDiagnosing ? "Diagnosing..." : "Submit"}
                  </Button>
                </Box>
                {diagnosticResult && (
                  <Box sx={{ mt: 2 }}>
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{ "& .MuiAlert-message": { width: "100%" } }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        Diagnosis
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {diagnosticResult.cause}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        Solution
                      </Typography>
                      <Box
                        sx={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}
                        dangerouslySetInnerHTML={{
                          __html: diagnosticResult.solution.replace(
                            /\*\*(.*?)\*\*/g,
                            "<strong>$1</strong>",
                          ),
                        }}
                      />
                    </Alert>
                  </Box>
                )}
              </Paper>
            </>
          )}


          {/* Danger zone */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)",
              border: "none",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Reset local workflow
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Clears draft, approved snapshot, generated code, and validation
              history from this browser.
            </Typography>
            <Button
              color="warning"
              variant="outlined"
              onClick={() => {
                clearAllLocalWorkflow();
                setRawIntake("");
                setIntentSummary(null);
                setIntakeContract(null);
                setIntakeError(null);
                setMapText("");
                setResult(null);
                setParseError(null);
                setApproved(false);
                setApprovedLockedJson(null);
                setGenerated(null);
                setMonitoring(null);
                setHistory([]);
                setLastAnalyzedText(null);
                setMapGenerated(false);
                setValidatedText(null);
                intelProgress.reset();
                mapProgress.reset();
                setNotification({
                  type: "success",
                  message: "Local data cleared successfully!",
                });
              }}
            >
              Clear all local data
            </Button>
          </Paper>
        </Stack>
      </ActivityPanel>

      {/* Custom Quote Form Modal */}
      <Dialog open={quoteFormOpen} onClose={() => setQuoteFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request a Custom Quote</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please fill out the details below. We'll automatically include your Blueprint and Intent Summary in the request.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            value={quoteFormData.name}
            onChange={(e) => {
              setQuoteFormData({ ...quoteFormData, name: e.target.value });
              if (quoteFormErrors.name) setQuoteFormErrors({ ...quoteFormErrors, name: "" });
            }}
            error={!!quoteFormErrors.name}
            helperText={quoteFormErrors.name}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={quoteFormData.email}
            onChange={(e) => {
              setQuoteFormData({ ...quoteFormData, email: e.target.value });
              if (quoteFormErrors.email) setQuoteFormErrors({ ...quoteFormErrors, email: "" });
            }}
            error={!!quoteFormErrors.email}
            helperText={quoteFormErrors.email}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={quoteFormData.phone}
            onChange={(e) => {
              setQuoteFormData({ ...quoteFormData, phone: e.target.value });
              if (quoteFormErrors.phone) setQuoteFormErrors({ ...quoteFormErrors, phone: "" });
            }}
            error={!!quoteFormErrors.phone}
            helperText={quoteFormErrors.phone}
            placeholder="+1 (555) 000-0000"
            sx={{ mb: 2 }}
          />
          {/* Read-only reference of context being sent */}
          <Accordion variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight="bold">Attached Context (Blueprint & Query)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                <strong>Your Query:</strong>
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, mb: 1, maxHeight: 100, overflow: 'auto', bgcolor: 'grey.50' }}>
                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                  {rawIntake || "No query provided"}
                </Typography>
              </Paper>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                <strong>Generated Blueprint:</strong>
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 150, overflow: 'auto', bgcolor: 'grey.50' }}>
                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                  {approvedLockedJson || mapText || "No blueprint generated"}
                </Typography>
              </Paper>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteFormOpen(false)} disabled={isSubmittingQuote}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const errors = {};
              if (!quoteFormData.name.trim()) errors.name = "Name is required";
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!quoteFormData.email.trim()) {
                errors.email = "Email is required";
              } else if (!emailRegex.test(quoteFormData.email)) {
                errors.email = "Invalid email format";
              }
              const phoneRegex = /^\+?[\d\s\-\(\)]{7,20}$/;
              if (!quoteFormData.phone.trim()) {
                errors.phone = "Phone number is required";
              } else if (!phoneRegex.test(quoteFormData.phone)) {
                errors.phone = "Invalid phone number format";
              }

              if (Object.keys(errors).length > 0) {
                setQuoteFormErrors(errors);
                return;
              }

              setIsSubmittingQuote(true);
              try {
                await submitQuoteRequest({
                  name: quoteFormData.name.trim(),
                  email: quoteFormData.email.trim(),
                  phone: quoteFormData.phone.trim(),
                  query: rawIntake || "",
                  blueprint: approvedLockedJson || mapText || "",
                });
                setQuoteFormOpen(false);
                setNotification({
                  type: "success",
                  message: "Quote request submitted successfully!",
                });
              } catch (err) {
                setNotification({
                  type: "error",
                  message:
                    err?.response?.data?.detail ||
                    "Failed to submit quote request. Please try again.",
                });
              } finally {
                setIsSubmittingQuote(false);
              }
            }}
            disabled={isSubmittingQuote}
          >
            {isSubmittingQuote ? <CircularProgress size={24} /> : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function MonitorTable({ title, rows }) {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {title}: no rows.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Phase</TableCell>
            <TableCell>Rule</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Sev</TableCell>
            <TableCell>Message</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Fix</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.phase}</TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {r.rule_id}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", fontSize: 10 }}
                >
                  {r.rule_name}
                </Typography>
              </TableCell>
              <TableCell>{r.rule_type}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={r.severity}
                  color={
                    r.severity === "critical" || r.severity === "major"
                      ? "error"
                      : r.severity === "pass"
                        ? "success"
                        : r.severity === "informational"
                          ? "info"
                          : "default"
                  }
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    fontWeight:
                      r.severity === "major" || r.severity === "critical"
                        ? 600
                        : 400,
                  }}
                >
                  {r.message}
                </Typography>
                {r.why_it_matters && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", fontStyle: "italic", mt: 0.5 }}
                  >
                    Why: {r.why_it_matters}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>{r.location}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{r.suggested_fix}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
